// cspell:ignore supabase
import { supabase } from './supabase';

// Types for Explore Categories
export interface CMSExploreCategory {
  id?: string;
  title_key: string;
  title_en?: string;
  title_ar?: string;
  title_fr?: string;
  image_url: string;
  /** @deprecated Legacy fallback — use target_category_key instead */
  category_key?: string;
  /** Optional internal slug for the card (independent of navbar taxonomy) */
  card_key?: string;
  /** Primary category segment for sitemap routing */
  target_category_key?: string;
  /** Optional subcategory segment for sitemap routing */
  target_subcategory_key?: string;
  /** Optional full-path override; takes precedence over target mapping */
  link_url?: string;
  is_active?: boolean;
  display_order?: number;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}

class ExploreCategoriesServiceClass {
  private isMissingRelationError(error: any): boolean {
    if (!error) return false;
    const code = error.code || '';
    const status = error.status || 0;
    return code === 'PGRST205' || code === '42P01' || status === 404 || status === 406;
  }

  async getCategories(activeOnly = false) {
    try {
      let query = supabase.from('cms_explore_categories').select('*');
      
      if (activeOnly) {
        query = query.eq('is_active', true);
      }
      
      query = query.order('display_order', { ascending: true });
      
      const { data, error } = await query;
      if (error) {
        if (this.isMissingRelationError(error)) {
          return { data: [], error: null };
        }
        throw error;
      }
      return { data, error: null };
    } catch (error: any) {
      if (!this.isMissingRelationError(error)) {
        console.error('Error fetching explore categories:', error);
      }
      return { data: [], error: null };
    }
  }

  async getCategoryById(id: string) {
    try {
      const { data, error } = await supabase
        .from('cms_explore_categories')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching explore category:', error);
      return { data: null, error };
    }
  }

  async getCategoryByKey(categoryKey: string) {
    try {
      const { data, error } = await supabase
        .from('cms_explore_categories')
        .select('*')
        .eq('category_key', categoryKey)
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching explore category by key:', error);
      return { data: null, error };
    }
  }

  async createCategory(category: CMSExploreCategory) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('cms_explore_categories')
        .insert({
          ...category,
          created_by: user?.id,
          updated_by: user?.id
        })
        .select()
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating explore category:', error);
      return { data: null, error };
    }
  }

  async updateCategory(id: string, updates: Partial<CMSExploreCategory>) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('cms_explore_categories')
        .update({
          ...updates,
          updated_by: user?.id
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating explore category:', error);
      return { data: null, error };
    }
  }

  async deleteCategory(id: string) {
    try {
      const { error } = await supabase
        .from('cms_explore_categories')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting explore category:', error);
      return { error };
    }
  }

  async reorderCategories(orderedIds: string[]) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const updates = orderedIds.map((id, index) => ({
        id,
        display_order: index + 1,
        updated_by: user?.id
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from('cms_explore_categories')
          .update({ display_order: update.display_order, updated_by: update.updated_by })
          .eq('id', update.id);
        
        if (error) throw error;
      }

      return { error: null };
    } catch (error) {
      console.error('Error reordering explore categories:', error);
      return { error };
    }
  }
}

export const exploreCategoriesService = new ExploreCategoriesServiceClass();